import { useCallback } from 'react';
import { useAuth } from '../../../../../auth/context/native/AuthContext';
import type { OnActionArgs, OnActionType } from '../../../../context/ActionsProvider/context';
import { removeDataBindingBraces } from '../../../../utils/dynamicBindings';
import { indirectExecuteScript } from '../../../../utils/indirectEval';
import { safeSetInterval, safeSetTimeout } from '../../../../utils/safeTimers';
import { useReportActionError } from '../../../useReportActionError';
import { useRouter } from '../../../useRouter';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';

// labels the user script's sourceURL so a thrown stack points at user code, not the executor wrapper
const USER_SCRIPT_SOURCE_MARKER = 'ua_user_script.js';

const windowVarsRegex = /\bwindow\.(?<temp1>\w+)\b/g;

function findWindowProps(inputString: string) {
  const matches: string[] = [];

  let match: RegExpExecArray | null;
  while ((match = windowVarsRegex.exec(inputString)) !== null) {
    matches.push(match[1]);
  }

  return matches;
}

export const useExecuteScript = () => {
  const router = useRouter();
  const { checkAuth } = useAuth();
  const reportActionError = useReportActionError();

  const onExecuteScript: OnActionType = useCallback(
    ({ action, actionContext, blockId, instanceId }: OnActionArgs) => {
      const script = action.payload?.script as string | undefined;

      try {
        if (script) {
          indirectExecuteScript(
            `${removeDataBindingBraces(script)}\n//# sourceURL=${USER_SCRIPT_SOURCE_MARKER}`,
            {
              ...actionContext,
              router,
              checkAuth,
              setTimeout: safeSetTimeout,
              setInterval: safeSetInterval,
            },
            findWindowProps(script),
          );
        }
      } catch (err) {
        console.error('Error evaluating script: ', err);
        // surface the swallowed script error to Pulse; without this the action only ever shows ok
        reportActionError({
          source: 'runScript',
          error: err,
          instanceId,
          blockId,
          expression: script,
        });
      }

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [checkAuth, router, reportActionError],
  );

  return { onExecuteScript };
};
